import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Table, Tab, Nav } from 'react-bootstrap';
import * as pdfjsLib from 'pdfjs-dist/webpack'; // Import PDF.js
import './PreviewModel.css';

export default function ExcelPreviewModal({ show, onHide, excelData, fileName, fileURL }) {
    const fileExtension = fileName.split('.').pop().toLowerCase();

    const isImage = ["png", "jpg", "jpeg", "gif"].includes(fileExtension);
    const isPDF = fileExtension === "pdf";
    const isExcel = ["xls", "xlsx"].includes(fileExtension);

    const [activeTab, setActiveTab] = useState("");
    const pdfContainerRef = useRef(null); // Ref to hold the PDF render container

    // Set default active tab on data load
    useEffect(() => {
        if (isExcel && Array.isArray(excelData) && excelData.length > 0) {
            setActiveTab(excelData[0].sheetName);
        }
    }, [excelData]);

    useEffect(() => {
        if (isPDF && fileURL && pdfContainerRef.current) {
            // Use PDF.js to render the PDF
            const loadingTask = pdfjsLib.getDocument(fileURL);

            loadingTask.promise.then(pdf => {
                // Render the first page of the PDF (or you can render all pages as needed)
                pdf.getPage(1).then(page => {
                    const scale = 1.5; // Adjust scale as necessary
                    const viewport = page.getViewport({ scale });

                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    canvas.width = viewport.width;
                    canvas.height = viewport.height;

                    // Append the canvas to the modal's PDF container
                    pdfContainerRef.current.innerHTML = ''; // Clear any existing content
                    pdfContainerRef.current.appendChild(canvas);

                    // Render the page into the canvas
                    page.render({
                        canvasContext: context,
                        viewport: viewport,
                    });
                });
            }).catch(error => {
                console.error("Error loading PDF:", error);
            });
        }
    }, [isPDF, fileURL]);

    return (
        <Modal show={show} onHide={onHide} centered size="xl" dialogClassName="responsive-modal">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '1.2rem' }}>
                    Preview: {fileName.replace(/^.*?_/, '')}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {isImage ? (
                    <div className="text-center">
                        <img
                            src={fileURL}
                            alt={fileName}
                            className="preview-image"
                        />
                    </div>
                ) : isPDF ? (
                    <div ref={pdfContainerRef} style={{ width: '100%', overflowY: 'auto' }}>

                    </div>
                ) : isExcel && Array.isArray(excelData) && excelData.length > 0 ? (
                    <>
                        <div className="custom-tab-wrapper">
                            <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                                {excelData.map((sheet, idx) => (
                                    <Nav.Item key={idx}>
                                        <Nav.Link eventKey={sheet.sheetName}>{sheet.sheetName}</Nav.Link>
                                    </Nav.Item>
                                ))}
                            </Nav>
                        </div>

                        <Tab.Content className="mt-3">
                            {excelData.map((sheet, idx) => (
                                <Tab.Pane eventKey={sheet.sheetName} key={idx} active={activeTab === sheet.sheetName}>
                                    <div className="preview-content-wrapper" style={{ overflowX: 'auto' }}>
                                        <Table striped bordered hover responsive className="dark-bordered-table excel-preview-table">
                                            <tbody>
                                                {sheet.data
                                                    .filter(row => row.some(cell => cell !== undefined && cell !== null && String(cell).trim() !== ""))
                                                    .map((row, i) => {
                                                        const maxColumns = Math.max(...sheet.data.map(r => r.length));
                                                        return (
                                                            <tr key={i}>
                                                                {Array.from({ length: maxColumns }).map((_, j) => (
                                                                    <td key={j}>{row[j] !== undefined ? row[j] : ""}</td>
                                                                ))}
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Tab.Pane>
                            ))}
                        </Tab.Content>

                    </>
                ) : (
                    <p>No preview available for this file.</p>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close</Button>
                <Button
                    variant="primary"
                    onClick={() => {
                        const link = document.createElement('a');
                        link.href = fileURL;
                        link.setAttribute('download', fileName);
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }}
                >
                    Download
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
